import {post} from '@/utils/request';
import {
  isPublicLoginEntry,
  requireLoginContextToken,
  withLoginContext,
} from '@/apis/organizationContext';

// A learner login URL may carry the Workspace organization that issued the
// account. Missing means the legacy personal scope; a malformed supplied value
// is sent as invalid so the server rejects it instead of silently falling back.
export function organizationScopeFromLocation(): string | null {
  const raw = new URLSearchParams(window.location.search).get('organization_id');
  if (raw === null) {
    return null;
  }
  if (!/^[1-9]\d*$/.test(raw)) {
    return '-1';
  }
  return raw;
}

function postWithOrganizationScope<T>(url: string, payload: Record<string, unknown>) {
  if (isPublicLoginEntry()) {
    return post<T>(url, {...payload, organization_auth_version: 1});
  }
  return withLoginContext(loginContext => post<T>(url, {...payload, login_context: loginContext}));
}

// QR state and subsequent binding tickets are bound to the exact issued token.
const wechatLoginContexts = new Map<string, string>();
const wechatBindContexts = new Map<string, string>();

export interface IStudentIdentity {
  organization_id: string;
  organization_code: string;
  organization_name: string;
  login_account: string;
}

export interface IOrganizationSelection {
  identities: IStudentIdentity[];
  selection_ticket: string;
}

export interface IPasswordResetAccount {
  student_id: number;
  account: string;
  organization_id: string;
  organization_code: string;
  organization_name: string;
}
export interface ICaptchaData {
  captcha_key: string;
  code: number;
  image_base64: string;
  tile_base64: string;
  tile_height: number;
  tile_width: number;
  tile_x: number;
  tile_y: number;
}

export function sendCode(phone: string, countryId: number, value?: string, code?: string, type?: number, key?: string, point?: number[]) {
  return postWithOrganizationScope<{
    result: boolean;
    success: boolean;
    captcha: string;
    captcha_code: string;
    captcha_data?: ICaptchaData;
  }>('/api/student/send-sms', {
    country_id: countryId,
    mobile: phone,
    captcha_value: value,
    captcha_code: code,
    captcha_type: type,
    key,
    point,
  });
}

export function sendResetPasswordSMS(phone: string, countryId: number, value?: string, code?: string, type?: number, key?: string, point?: number[]) {
  return postWithOrganizationScope<{
    success: boolean;
    captcha: string;
    captcha_code: string;
    captcha_data?: ICaptchaData;
  }>('/api/student/reset-password-send-sms', {
    country_id: countryId,
    mobile: phone,
    captcha_value: value,
    captcha_code: code,
    captcha_type: type,
    key,
    point,
  });
}

export function checkResetPasswordSMS(phone: string, countryId: number, code?: string) {
  return postWithOrganizationScope<{
    result: boolean;
    accounts?: IPasswordResetAccount[];
    reset_selection_ticket?: string;
  }>('/api/student/check-reset-password-sms', {
    mobile: phone,
    country_id: countryId,
    code,
  });
}

export function sendBindMobileSMS(phone: string, countryId: number, value?: string, code?: string, type?: number, key?: string, point?: number[]) {
  return post<{
    result: boolean;
    captcha: string;
    captcha_code: string;
    captcha_data?: ICaptchaData;
  }>('/api/student/send-student-sms', {
    country_id: countryId,
    mobile: phone,
    captcha_value: value,
    captcha_code: code,
    captcha_type: type,
    key,
    point,
  });
}

export function checkBindMobileSMS(phone: string, countryId: number, code?: string) {
  return post<{
    result: boolean;
  }>('/api/student/account-bind-mobile', {
    mobile: phone,
    country_id: countryId,
    code,
  });
}

export function checkPassword(
  phone: string,
  countryId: number,
  password: string,
  submit_password: string,
  resetSelectionTicket?: string,
  targetStudentId?: number,
) {
  return postWithOrganizationScope<{
    result: boolean;
  }>('/api/student/reset-password', {
    mobile: phone,
    country_id: countryId,
    password,
    submit_password,
    reset_selection_ticket: resetSelectionTicket,
    target_student_id: targetStudentId,
  });
}



// 验证码登录
// code: 2001 验证码错误
export function codeLogin(phone: string, countryId: number, code: string, isProlong: boolean, invite_code: string) {
  return postWithOrganizationScope<{
    token: string;
    identities?: IStudentIdentity[];
    selection_ticket?: string;
  }>('/api/student/login', {
    mobile: phone,
    code,
    is_prolong: isProlong ? 1 : 2,
    country_id: countryId,
    invite_code,
  });
}

export function checkCode(phone: string, code: string) {
  return post<null>('/api/student/check-sms', {
    mobile: phone,
    code,
  });
}

export function captchaCreate() {
  return post<{
    id: string;
    // image base64 string
    captcha: string;
  }>('/api/captcha/create', {});
}

export function captchaVerify(id: string, code: string) {
  return post<{
    result: boolean;
  }>('/api/captcha/verify', {
    id, code
  });
}


// 是否需要图片验证码
export function needCaptchaAPI(phone: string) {
  return post<{
    is_check: boolean;
  }>('/api/student/is-check-captcha', {
    mobile: phone,
  });
}

export function getCountry() {
  return post<{
    list: Array<{
        id: number;
        name: string;
        code: string;
        emoji: string;
    }>;
  }>('/api/common/get-country-list', {});
}

export type BusinessLoginMethod = 'sms' | 'account' | 'wechat';

export type BusinessProtocolType = 'user_agreement' | 'privacy_policy' | 'disclaimer' | 'copyright_statement';

export type BusinessProtocolMode = 'default' | 'link' | 'content';
export type BusinessBackgroundFit = 'cover' | 'contain';
export type BusinessBackgroundPosition =
  | 'top_left'
  | 'top'
  | 'top_right'
  | 'left'
  | 'center'
  | 'right'
  | 'bottom_left'
  | 'bottom'
  | 'bottom_right';

export interface IBusinessProtocolConfig {
  title?: string;
  mode?: BusinessProtocolMode;
  url?: string;
  content?: string;
}

export interface IBusinessLoginPageConfig {
  org_key: string;
  company_name: string;
  login_title: string;
  account_prefix: string;
  logo_url: string;
  background_url: string;
  background_render?: {
    fit?: BusinessBackgroundFit;
    position?: BusinessBackgroundPosition;
    color?: string;
  };
  login_methods: BusinessLoginMethod[];
  default_login_method: BusinessLoginMethod;
  config_source?: 'organization' | 'ancestor' | 'default';
  protocols?: Partial<Record<BusinessProtocolType, IBusinessProtocolConfig>>;
}

export function getBusinessLoginPageConfig(
  orgKey: string,
  preview?: {
    preview_version?: number;
    preview_token?: string;
  },
) {
  return post<IBusinessLoginPageConfig | null>('/api/student/get-business-login-page-config', {
    org_key: orgKey,
    preview_version: preview?.preview_version,
    preview_token: preview?.preview_token,
  });
}

// 获取生成微信二维码的信息
export async function getWechatLoginState() {
  return withLoginContext(async loginContext => {
    const result = await post<{
      redirect_uri: string;
      code: string;
      app_id: string;
    }>('/api/student/get-wechat-login', {login_context: loginContext});
    wechatLoginContexts.set(result.data.code, loginContext);
    return result;
  });
}


export function getWechatBindState() {
  return post<{
    redirect_uri: string;
    code: string;
    app_id: string;
  }>('/api/student/get-wechat-bind');
}

// 轮询获取扫码状态
export async function getWechatQrcodeStatus(code: string, is_prolong: boolean) {
  const loginContext = wechatLoginContexts.get(code) ?? requireLoginContextToken();
  const result = await post<{
    'wechat_id': string;
	'wechat_ticket': string;
    'student_id': number;
    'token': string;
    'is_bind_mobile': number;
  }>('/api/student/get-wechat-authorize', {
    code, is_prolong: is_prolong ? 1 : 2, login_context: loginContext,
  });
  if (result.data.wechat_ticket) {
    wechatBindContexts.set(result.data.wechat_ticket, loginContext);
  }
  return result;
}


export function getWechatQrcodeBindStatus(code: string) {
  return post<{
    result: 'success' | 'failed' | 'exist' | 'not';
    wechat_id: string;
    wechat_name: string;
  }>('/api/student/get-wechat-bind-authorize', {
    code
  });
}


export function bindMobile(phone: string, countryId: number, code: string, wechatId: string, wechatTicket: string, isProlong: boolean, invite_code: string) {
  return post<{
    token: string;
  }>('/api/student/bind-mobile', {
    country_id: countryId,
    mobile: phone,
    code,
    wechat_id: wechatId,
    wechat_ticket: wechatTicket,
    is_prolong: isProlong ? 1 : 2,
    invite_code,
    login_context: wechatBindContexts.get(wechatTicket) ?? requireLoginContextToken(),
  });
}

/**
 * 账号密码登录
 * @param account 
 * @param password 
 * @param isProlong 
 * @returns 
 */
export function accountLogin(account: string, password: string, isProlong: boolean) {
  return postWithOrganizationScope<{
    token: string;
    is_bind_mobile: number;
    identities?: IStudentIdentity[];
    selection_ticket?: string;
  }>('/api/student/account-login', {
    account,
    password,
    is_prolong: isProlong ? 1 : 2
  });
}

export function selectOrganization(selectionTicket: string, organizationId: string, isProlong: boolean) {
  return post<{
    token: string;
    is_bind_mobile: number;
  }>('/api/student/select-organization', {
    selection_ticket: selectionTicket,
    organization_id: organizationId,
    is_prolong: isProlong ? 1 : 2,
  });
}


export const checkToken = () => {
  return post<{token: string;}>('/api/student/check-token', {});
};

export interface IUserInfoApiResData {
  account: string;
  organization?: {
    organization_id: string;
    /** The top-level organization which owns the student's account namespace. */
    account_organization_id?: string;
    organization_code: string;
    organization_name: string;
    is_default: boolean;
  };
}

export const getUserInfo = async () => {
  return post<IUserInfoApiResData>('/api/student/get-info', {});
};
